import { api } from './api';
import { isDeviceReallyOn, subscriptionKeyMatches, type PushDeviceState } from '../utils/pushDeviceModel';

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** FIX-BUG-CARD-PUSH: never wait forever for a service worker that is not registered (then: no push here). */
async function swReady(timeoutMs = 8000): Promise<ServiceWorkerRegistration | null> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => window.setTimeout(() => resolve(null), timeoutMs)),
  ]);
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await swReady();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

/** This browser's own subscription endpoint (null = none), so the server can say whether THIS browser is registered. */
export async function getCurrentPushEndpoint(): Promise<string | null> {
  if (!isPushSupported()) return null;
  try {
    return (await currentSubscription())?.endpoint ?? null;
  } catch {
    return null;
  }
}

async function saveOnServer(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await api.subscribePush({
    endpoint: json.endpoint!,
    keys: { p256dh: json.keys!.p256dh, auth: json.keys!.auth },
  });
}

/**
 * FIX-BUG-CARD-PUSH: the REAL state of this device: permission + a browser subscription made with the server's
 * current key + saved on the server for me. A browser that is allowed and subscribed with the right key but unknown
 * to the server is re-saved silently (no prompt is needed for that), so it heals on the next visit.
 */
export async function getThisDevicePushState(): Promise<PushDeviceState> {
  const base: PushDeviceState = {
    supported: isPushSupported(), permission: 'unsupported', browserSubscribed: false, keyMatches: null, registeredOnServer: null, serverConfigured: false,
  };
  if (!base.supported) return base;
  base.permission = Notification.permission;
  let serverKey: string | null = null;
  try { serverKey = (await api.getVapidPublicKey()).data?.publicKey || null; } catch { /* unknown */ }
  base.serverConfigured = !!serverKey;
  let sub: PushSubscription | null = null;
  try { sub = await currentSubscription(); } catch { sub = null; }
  base.browserSubscribed = !!sub;
  base.keyMatches = sub ? subscriptionKeyMatches(sub.options?.applicationServerKey ?? null, serverKey) : null;
  try {
    const st = (await api.getPushDeviceStatus(sub?.endpoint ?? null)).data;
    base.registeredOnServer = sub ? !!st?.this_device_registered : false;
  } catch { base.registeredOnServer = null; }
  if (sub && base.permission === 'granted' && base.keyMatches !== false && base.registeredOnServer === false) {
    try {
      await saveOnServer(sub);
      base.registeredOnServer = true;
    } catch { /* stays not registered: the bar asks */ }
  }
  return base;
}

/** True only when notices really reach this browser (see getThisDevicePushState). */
export async function isPushSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    return isDeviceReallyOn(await getThisDevicePushState());
  } catch {
    return false;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** DQ-239: why a subscribe attempt did not end subscribed (so the screen can explain). */
export type PushSubscribeResult = { ok: true } | { ok: false; reason: 'unsupported' | 'denied' | 'not_configured' | 'failed' };

export async function subscribeToPush(): Promise<boolean> {
  return (await subscribeToPushDetailed()).ok;
}

/**
 * FIX-BUG-CARD-PUSH: the browser's permission window is opened FIRST, straight from the click (nothing awaited
 * before it, so the browser treats it as the user's action). Only after "granted": subscribe with the server's key
 * (an old subscription made with another key is replaced) and save it on the server.
 * `permission` lets a caller that already asked (in the same click) pass the answer in.
 */
export async function subscribeToPushDetailed(permissionPromise?: Promise<NotificationPermission>): Promise<PushSubscribeResult> {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  const permission = await (permissionPromise ?? Notification.requestPermission());
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  try {
    const vapidRes = await api.getVapidPublicKey();
    const publicKey = vapidRes.data?.publicKey;
    if (!publicKey) return { ok: false, reason: 'not_configured' };

    const reg = await swReady();
    if (!reg) {
      console.error('Push subscribe failed: no service worker');
      return { ok: false, reason: 'failed' };
    }
    let subscription = await reg.pushManager.getSubscription();
    if (subscription && subscriptionKeyMatches(subscription.options?.applicationServerKey ?? null, publicKey) === false) {
      // made with keys the server no longer has: every notice to it is refused (403) - replace it
      await subscription.unsubscribe().catch(() => undefined);
      subscription = null;
    }
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });
    }
    await saveOnServer(subscription);
    return { ok: true };
  } catch (err) {
    console.error('Push subscribe failed:', err);
    return { ok: false, reason: 'failed' };
  }
}

/** Ask the browser's permission synchronously inside a click handler (before any await). */
export function requestPushPermissionNow(): Promise<NotificationPermission> {
  if (!isPushSupported()) return Promise.resolve('denied');
  try {
    return Notification.requestPermission();
  } catch {
    return Promise.resolve(Notification.permission);
  }
}

export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const subscription = await currentSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await api.unsubscribePush(endpoint);
    }
    return true;
  } catch (err) {
    console.error('Push unsubscribe failed:', err);
    return false;
  }
}
